<template>
    <v-card v-if="user?.is_subscribed || user?.is_admin">
        <v-card-text>
            <v-row>
                <v-col cols="6">
                    <club-selector v-model="selectedClub" clearable />
                </v-col>

                <v-col cols="6">
                    <round-selector v-model="selectedRound" :rounds="meet.rounds" />
                </v-col>
            </v-row>

            <v-row v-if="isLoadingResults">
                <v-col cols="12">
                    <v-skeleton-loader type="list-item-avatar, divider, list-item-three-line" />
                    <v-skeleton-loader type="list-item-avatar, divider, list-item-three-line" class="mt-2" />
                    <v-skeleton-loader type="list-item-avatar, divider, list-item-two-line" class="mt-2" />
                </v-col>
            </v-row>

            <v-row no-gutters>
                <v-col cols="12">
                    <result-card
                        v-for="result in results"
                        :key="result.id"
                        :meet="meet"
                        :round="selectedRound"
                        :result="result"
                        show-division-info
                        hide-video
                        @click="$inertia.visit($route('result-categories.show', result.category_id))"
                    />
                </v-col>
            </v-row>
        </v-card-text>
    </v-card>

    <v-card v-else>
        <v-card-text>
            <subscription-required />
        </v-card-text>
    </v-card>
</template>

<script>
import numeral from 'numeral';
import orderBy from 'lodash/orderBy.js';
import Club from '@/Models/Club.js';
import { eventFromProscore, routineRelationship } from '@/helpers.js';
import ClubSelector from '@/Components/ClubSelector.vue';
import Result from '@/Models/Result.js';
import ResultCard from '@/Components/Results/ResultCard.vue';
import Meet from '@/Models/Meet.js';
import RoundSelector from '@/Components/RoundSelector.vue';
import User from '@/Models/User.js';
import SubscriptionRequired from '@/Components/SubscriptionRequired.vue';
import QueryBuilder from '@/query-builder.js';

export default {
    name: 'ClubMeetResults',

    components: { SubscriptionRequired, RoundSelector, ResultCard, ClubSelector },

    props: {
        meetId: {
            type: Number,
            required: true,
        },
    },

    data: () => ({
        selectedClub: null,
        isLoadingResults: false,
        selectedRound: 'prelims',
    }),

    computed: {
        clubs() {
            return Club.all();
        },

        meet() {
            return Meet.find(this.meetId);
        },

        user() {
            if (!this.$page.props.auth.user) {
                return null;
            }

            return User.query().with('affiliations').find(this.$page.props.auth.user.id);
        },

        results() {
            const results = Result.query()
                .with([
                    'athlete',
                    'athlete2',
                    'club.country',
                    'club2',
                    'category',
                    'trampoline_scores',
                    'double_mini_scores',
                    'tumbling_scores',
                    'synchro_scores',
                ])
                .where('meet_id', this.meetId)
                .where(result => {
                    if (!this.selectedClub?.id) {
                        return true;
                    }

                    return result.club_id === this.selectedClub?.id || result.club_2_id === this.selectedClub?.id;
                })
                .get();

            return orderBy(results, result => result.latest_score?.updated_at, 'desc');
        },
    },

    watch: {
        selectedClub() {
            this.retrieveExistingResults();
        },
    },

    async created() {
        if (!this.user) {
            return;
        }

        this.$echo.channel(`meet-${this.meetId}-results`).listen('ResultUpdated', this.resultUpdatedListener);
        this.$echo.channel(`meet-${this.meetId}-results`).listen('ResultPlacementUpdated', this.resultUpdatedListener);

        const clubAffiliations = this.user.affiliations.filter(
            affiliation => affiliation.affiliate_type_entity === 'clubs',
        );

        if (clubAffiliations.length >= 1) {
            this.selectedClub = Club.find(clubAffiliations[0].affiliate_id);
        }
    },

    beforeDestroy() {
        this.$echo.leave(`meet-${this.meetId}-results`);
    },

    methods: {
        eventFromProscore,
        numeral,

        async retrieveExistingResults() {
            const includes = [
                'athlete',
                'athlete2',
                'club.country',
                'club2',
                'category',
                'trampolineScores',
                'doubleMiniScores',
                'tumblingScores',
                'synchroScores',
            ];

            this.isLoadingResults = true;

            const query = new QueryBuilder('results')
                .filter('meet_id', this.meetId)
                .when(this.selectedClub?.id, query => query.filter('either_club', this.selectedClub.id))
                .sortDesc('updated_at')
                .include(includes);

            await Result.api().get(query.toUrl());

            this.isLoadingResults = false;
        },

        async resultUpdatedListener(e) {
            console.log({ result: e.result });

            if (
                this.selectedClub &&
                e.result.club_id !== this.selectedClub.id &&
                e.result.club_2_id !== this.selectedClub.id
            ) {
                return;
            }

            const includes = [
                'athlete',
                'athlete2',
                'club.country',
                'club2',
                'category',
                routineRelationship(e.result.event),
            ];

            await Result.api().get(`/api/results/${e.result.id}?include=${includes.join(',')}`);
        },
    },
};
</script>
